/**
 * Utility functions for thermal printer (80mm) printing
 */

export interface ThermalPrintOptions {
  title?: string;
  autoPrint?: boolean;
}

/**
 * Opens a print window optimized for 80mm thermal printers.
 *
 * Fix I: Accepts an optional pre-opened window reference to avoid browser popup
 * blockers. Callers should open the window SYNCHRONOUSLY during a user gesture
 * (before any await), then pass it here after async operations complete.
 */
export function printThermalContent(
  htmlContent: string,
  options: ThermalPrintOptions = {},
  preOpenedWindow?: Window | null,
) {
  const { title = 'Cupom Fiscal', autoPrint = false } = options;

  const printWindow = preOpenedWindow || window.open('', '_blank', 'width=320,height=600');
  if (!printWindow) {
    console.error('Could not open print window');
    return;
  }

  const thermalCSS = `
    @page {
      size: 80mm auto;
      margin: 0;
    }
    
    /* ===========================================
       OTIMIZAÇÃO PARA IMPRESSORAS TÉRMICAS
       Problema: Anti-aliasing causa tons de cinza
       Solução: Preto absoluto + maior peso de fonte
       =========================================== */
    
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
      /* Desabilitar suavização de fonte para impressoras térmicas */
      -webkit-font-smoothing: none !important;
      -moz-osx-font-smoothing: unset !important;
      text-rendering: geometricPrecision !important;
      /* Remover sombras */
      text-shadow: none !important;
      box-shadow: none !important;
    }
    
    body {
      font-family: 'Courier New', 'Lucida Console', Monaco, monospace;
      font-size: 12px;
      font-weight: 600;
      width: 80mm;
      max-width: 80mm;
      padding: 4mm;
      background: #ffffff;
      color: #000000;
      line-height: 1.3;
      letter-spacing: 0.02em;
      filter: contrast(120%);
    }
    
    .coupon-header {
      text-align: center;
      margin-bottom: 8px;
      padding-bottom: 8px;
      border-bottom: 1.5px dashed #000000;
    }
    
    .company-name {
      font-size: 14px;
      font-weight: 800;
      margin-bottom: 4px;
      color: #000000;
    }
    
    .company-info {
      font-size: 10px;
      line-height: 1.4;
      color: #000000;
    }
    
    .section-title {
      font-weight: 800;
      text-align: center;
      margin: 8px 0 4px 0;
      padding: 4px 0;
      border-top: 1.5px dashed #000000;
      border-bottom: 1.5px dashed #000000;
      color: #000000;
    }
    
    .items-table {
      width: 100%;
      margin: 4px 0;
    }
    
    .item-row {
      display: flex;
      justify-content: space-between;
      padding: 2px 0;
      font-size: 11px;
      color: #000000;
    }
    
    .item-name {
      flex: 1;
      overflow-wrap: break-word;
      word-wrap: break-word;
      white-space: normal;
      max-width: 70%;
      line-height: 1.2;
      color: #000000;
    }
    
    .item-qty {
      width: 30px;
      text-align: center;
      color: #000000;
    }
    
    .item-value {
      width: 60px;
      text-align: right;
      color: #000000;
    }
    
    .totals-section {
      margin-top: 8px;
      padding-top: 8px;
      border-top: 1.5px dashed #000000;
    }
    
    .total-row {
      display: flex;
      justify-content: space-between;
      padding: 2px 0;
      color: #000000;
    }
    
    .total-row.grand-total {
      font-size: 14px;
      font-weight: 800;
      border-top: 2px solid #000000;
      padding-top: 4px;
      margin-top: 4px;
      color: #000000;
    }
    
    .payment-section {
      margin-top: 8px;
      padding-top: 8px;
      border-top: 1.5px dashed #000000;
    }
    
    .footer-section {
      margin-top: 12px;
      padding-top: 8px;
      border-top: 1.5px dashed #000000;
      text-align: center;
      font-size: 10px;
      color: #000000;
    }
    
    .nfce-info {
      margin-top: 8px;
      padding: 4px;
      border: 1.5px solid #000000;
      text-align: center;
      font-size: 10px;
      color: #000000;
    }
    
    .qrcode-placeholder {
      width: 100px;
      height: 100px;
      margin: 8px auto;
      border: 1.5px dashed #000000;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 9px;
      color: #000000;
    }
    
    .divider {
      border-bottom: 1.5px dashed #000000;
      margin: 8px 0;
    }
    
    .text-center {
      text-align: center;
    }
    
    .text-right {
      text-align: right;
    }
    
    .text-bold {
      font-weight: 800 !important;
      color: #000000;
    }
    
    .text-small {
      font-size: 10px;
      color: #000000;
    }
    
    @media print {
      * {
        color: #000000 !important;
        background: transparent !important;
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        color-adjust: exact !important;
        -webkit-font-smoothing: none !important;
        text-shadow: none !important;
        box-shadow: none !important;
      }
      
      body {
        width: 80mm;
        margin: 0;
        padding: 2mm;
        font-weight: 600 !important;
        filter: contrast(150%) !important;
        background: #ffffff !important;
      }
      
      .no-print {
        display: none !important;
      }
      
      /* Bordas mais visíveis na impressão */
      .divider,
      .coupon-header,
      .section-title,
      .totals-section,
      .payment-section,
      .footer-section,
      .nfce-info {
        border-color: #000000 !important;
      }
      
      .total-row.grand-total {
        border-color: #000000 !important;
      }
    }
    
    .print-buttons {
      position: fixed;
      bottom: 10px;
      left: 50%;
      transform: translateX(-50%);
      display: flex;
      gap: 8px;
      z-index: 1000;
    }
    
    .print-btn {
      padding: 10px 20px;
      font-size: 14px;
      cursor: pointer;
      border: none;
      border-radius: 4px;
    }
    
    .print-btn-primary {
      background: #22c55e;
      color: white;
    }
    
    .print-btn-secondary {
      background: #6b7280;
      color: white;
    }
  `;

  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=320">
        <title>${title}</title>
        <link rel="icon" href="/gonx-logo.svg" type="image/svg+xml">
        <style>${thermalCSS}</style>
      </head>
      <body>
        ${htmlContent}
        <div class="print-buttons no-print">
          <button class="print-btn print-btn-primary" onclick="window.print()">🖨️ Imprimir</button>
          <button class="print-btn print-btn-secondary" onclick="window.close()">Fechar</button>
        </div>
        ${autoPrint ? '<script>window.onload = function() { window.print(); }</script>' : ''}
      </body>
    </html>
  `);

  printWindow.document.close();
}

/**
 * Format currency for thermal print (shorter format)
 */
export function formatCurrencyThermal(value: number): string {
  return value.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/**
 * Format date/time for thermal print
 */
export function formatDateTimeThermal(date: Date = new Date()): string {
  return date.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}
